import { Suspense, lazy, useRef } from 'react';

import { Redirect, Route, Switch, matchPath, type RouteComponentProps } from 'react-router-dom';

import AuthenticatedRoute from '~/components/AuthenticatedRoute/AuthenticatedRoute';
import StopImpersonateButton from '~/components/StopImpersonateButton/StopImpersonateButton';
import { ORDER_SLUGS_WITH_NAV, ROUTES_WITHOUT_NAV } from '~/constants/routes';
import { challengeRoutePath } from '~/routes/challenge/challengeRoute.path';
import { forgotPasswordRoutePath } from '~/routes/forgot-password/forgotPasswordRoute.path';
import { getStartedRoutePath } from '~/routes/get-started/getStartedRoute.path';
import { loginRoutePath } from '~/routes/login/loginRoute.path';
import { oauthRoutePath } from '~/routes/oauth/oauthRoute.path';
import { onboardingRoutePath } from '~/routes/onboarding/onboardingRoute.path';
import { resetPasswordRoutePath } from '~/routes/reset-password/resetPasswordRoute.path';
import { signUpRoutePath } from '~/routes/sign-up/signUpRoute.path';

import { invoicesRoutePath } from './routes/settings/account/billing/invoices/invoicesRoute.path';

const GetStarted = lazy(() =>
  import('~/routes/get-started/GetStartedRoute').then(({ GetStartedRouteComponent }) => ({
    default: GetStartedRouteComponent,
  })),
);

const Invoices = lazy(() =>
  import('./routes/settings/account/billing/invoices/InvoicesRoute').then(
    ({ InvoicesRouteComponent }) => ({
      default: InvoicesRouteComponent,
    }),
  ),
);

const Onboarding = lazy(() =>
  import('~/routes/onboarding/OnboardingRoute').then(({ OnboardingRouteComponent }) => ({
    default: OnboardingRouteComponent,
  })),
);

const SignUpRouteComponent = lazy(() =>
  import('~/routes/sign-up/SignUpRoute').then(({ SignUpRouteComponent }) => ({
    default: SignUpRouteComponent,
  })),
);

const OauthRouteComponent = lazy(() =>
  import('~/routes/oauth/OauthRoute').then(({ OauthRouteComponent }) => ({
    default: OauthRouteComponent,
  })),
);

const ResetPasswordRouteComponent = lazy(() =>
  import('~/routes/reset-password/ResetPasswordRoute').then(({ ResetPasswordRouteComponent }) => ({
    default: ResetPasswordRouteComponent,
  })),
);

const Main = lazy(() => import('~/pages/Main'));

const StoreConnections = lazy(() => import('~/pages/StoreConnections/StoreConnections'));

const Challenge = lazy(() =>
  import('~/routes/challenge/ChallengeRoute').then(({ ChallengeRouteComponent }) => ({
    default: ChallengeRouteComponent,
  })),
);
const Login = lazy(() =>
  import('~/routes/login/LoginRoute').then(({ LoginRouteComponent }) => ({
    default: LoginRouteComponent,
  })),
);

const LoginSSO = lazy(() => import('~/pages/Login/LoginSSOFormPage'));

const ForgotPassword = lazy(() =>
  import('~/routes/forgot-password/ForgotPasswordRoute').then(
    ({ ForgotPasswordRouteComponent }) => ({
      default: ForgotPasswordRouteComponent,
    }),
  ),
);

const Root = lazy(() => import('~/pages/Root'));

const Close = lazy(() => import('~/pages/Close'));

// we need to parse slugs because our router is overloading the :ordersId slug in our route declarations
// ideally we would get the :orderId slug using react router but that value is not available in the react
// router params, possibly because we are overloading that slug value in our orders page route declarations
const getSlugFromPathname = (pathname: string) => pathname.split('/')[2];

// NOTE: the default for matchPath is exact:false, strict: false
// which means these matches will be more broad
const getShouldHideNav = (pathname: string): boolean => {
  const slug = getSlugFromPathname(pathname);
  const shouldSlugShowNav = ORDER_SLUGS_WITH_NAV.includes(slug);
  // convert to boolean for easier/more explicit typing
  const isMatchedRoute = !!matchPath(pathname, {
    path: ROUTES_WITHOUT_NAV,
  });

  return isMatchedRoute && !shouldSlugShowNav;
};

export const PATHS = {
  close: '/close',
  connect: '/connect',
  root: '/',
  ssoLogin: '/sso/login',
  userBilling: '/user/billing',
} as const;

const AppRoutes = () => {
  const renderMainLayout = useRef(({ location: { pathname } }: RouteComponentProps) => (
    <Main shouldHideNav={getShouldHideNav(pathname)} />
  )).current;

  return (
    <Suspense fallback="">
      <StopImpersonateButton />

      <Switch>
        <Route component={Close} exact path={PATHS.close} />
        <Route component={SignUpRouteComponent} exact path={signUpRoutePath} />
        <Route component={OauthRouteComponent} path={oauthRoutePath} />
        <Route component={ResetPasswordRouteComponent} exact path={resetPasswordRoutePath} />
        <Route component={Login} exact path={loginRoutePath} />
        <Route component={Challenge} exact path={[challengeRoutePath]} />
        <Route exact path={[PATHS.ssoLogin]}>
          <LoginSSO />
        </Route>
        <Redirect exact from="/forgot_password" to={forgotPasswordRoutePath} />
        <Redirect exact from="/sent" to={loginRoutePath} />
        <Route component={ForgotPassword} exact path={forgotPasswordRoutePath} />
        <AuthenticatedRoute component={StoreConnections} path={PATHS.connect} />
        <Redirect exact from="/verification_success" to={PATHS.root} />
        <Redirect exact from="/resend_verification" to={PATHS.root} />
        <Redirect exact from="/resend_verification/success" to={PATHS.root} />
        <AuthenticatedRoute component={Invoices} path={invoicesRoutePath} />
        <Redirect from={PATHS.userBilling} to={'/settings/account/billing'} />
        <AuthenticatedRoute component={GetStarted} path={getStartedRoutePath} />
        <AuthenticatedRoute component={Onboarding} path={onboardingRoutePath} />
        <AuthenticatedRoute component={Root} exact path={PATHS.root} />
        <Route render={renderMainLayout} />
      </Switch>
    </Suspense>
  );
};

export default AppRoutes;
